import React, { useState } from "react";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { PublicKey, Transaction } from "@solana/web3.js";
import {
  createSetAuthorityInstruction,
  AuthorityType,
} from "@solana/spl-token";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { useToast } from "../hooks/use-toast";
import { UserCheck, Shield, AlertTriangle, CheckCircle } from "lucide-react";
import { getTokenProgramId } from "../lib/spl-token-utils";

const AUTHORITY_TYPES = [
  {
    value: 0, // AuthorityType.MintTokens
    label: "Mint Authority",
    description: "Who can mint new tokens",
    icon: <Shield className="h-4 w-4" />,
    warning:
      "Transferring mint authority cannot be undone. The new authority will have full control over token minting.",
  },
  {
    value: 1, // AuthorityType.FreezeAccount
    label: "Freeze Authority",
    description: "Who can freeze or thaw accounts",
    icon: <UserCheck className="h-4 w-4" />,
    warning:
      "The new freeze authority will be able to freeze/unfreeze any token accounts.",
  },
];

const AuthorityManagement = ({ mintAddress, onAuthorityChanged, isAdmin }) => {
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const { toast } = useToast();

  const envMintAddress = process.env.REACT_APP_SPL_TOKEN_MINT_ADDRESS;
  const defaultMintAddress = envMintAddress || mintAddress;

  const [accountOrMintAddress, setAccountOrMintAddress] =
    useState(defaultMintAddress);
  const [selectedAuthority, setSelectedAuthority] = useState(null);
  const [newAuthorityAddress, setNewAuthorityAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [showPermanentPauseWarning, setShowPermanentPauseWarning] =
    useState(false);

  const selectedAuthorityType = AUTHORITY_TYPES.find(
    (type) => type.value === selectedAuthority
  );

  const processTransaction = async (
    transaction,
    blockhash,
    lastValidBlockHeight
  ) => {
    try {
      console.log("Simulating transaction...");
      const simulationResult = await connection.simulateTransaction(
        transaction
      );

      console.log("Simulation result:", simulationResult);

      if (simulationResult.value.err) {
        const errorMsg = JSON.stringify(simulationResult.value.err);
        console.error("Simulation failed:", errorMsg);

        if (errorMsg.includes("InsufficientFunds")) {
          throw new Error(
            "Insufficient SOL balance to pay for transaction fees"
          );
        } else if (errorMsg.includes("InvalidAuthority")) {
          throw new Error(
            "Current wallet does not have authority to perform this operation"
          );
        } else if (errorMsg.includes("AccountNotFound")) {
          throw new Error(
            "Token account or mint not found. Please verify the address."
          );
        } else {
          throw new Error(`Transaction simulation failed: ${errorMsg}`);
        }
      }
      console.log("✅ Simulation successful");
    } catch (simError) {
      console.error("Simulation error:", simError);
      if (
        simError instanceof Error &&
        simError.message.includes("Transaction simulation failed")
      ) {
        throw simError;
      }
      console.log(
        "Continuing despite simulation error (might be signature-related)"
      );
    }

    console.log("Sending transaction to wallet...");

    const signature = await sendTransaction(transaction, connection, {
      maxRetries: 3,
      preflightCommitment: "confirmed",
      skipPreflight: false,
    });

    console.log("Authority transfer transaction sent:", signature);

    console.log("Waiting for confirmation...");
    const confirmation = await connection.confirmTransaction(
      {
        signature,
        blockhash,
        lastValidBlockHeight,
      },
      "confirmed"
    );

    if (confirmation.value.err) {
      const errorMsg = JSON.stringify(confirmation.value.err);
      console.error("Transaction failed:", errorMsg);
      throw new Error(`Transaction failed: ${errorMsg}`);
    }

    console.log("✅ Authority transfer confirmed");

    toast({
      title: "Authority transferred successfully! 🎉",
      description: `${selectedAuthorityType?.label} has been transferred to the new address`,
      variant: "default",
    });

    setAccountOrMintAddress(defaultMintAddress);
    setSelectedAuthority(null);
    setNewAuthorityAddress("");
    setShowWarning(false);
    setUseCustomAddress(false);

    onAuthorityChanged?.();
  };

  React.useEffect(() => {
    if (!useCustomAddress && selectedAuthority !== null) {
      setAccountOrMintAddress(defaultMintAddress);
    }
  }, [selectedAuthority, useCustomAddress, defaultMintAddress]);

  const isAddressEditable = () => useCustomAddress;

  const getAddressLabel = () => "Token Mint Address";

  const getAddressPlaceholder = () => "Auto-filled from environment";

  const getAddressHelpText = () => {
    if (!selectedAuthority) {
      return "Using mint address from environment variables for all authority operations.";
    }
    return `Using mint address from environment (${
      envMintAddress ? "configured" : "not configured"
    }).`;
  };

  const validateInputs = () => {
    if (!accountOrMintAddress.trim())
      return "Account or mint address is required";
    if (!selectedAuthority) return "Authority type must be selected";
    if (!newAuthorityAddress.trim()) return "New authority address is required";

    try {
      new PublicKey(accountOrMintAddress);
    } catch {
      return "Invalid account or mint address";
    }

    try {
      new PublicKey(newAuthorityAddress);
    } catch {
      return "Invalid new authority address";
    }

    return null;
  };

  const handleTransferAuthority = async () => {
    if (!publicKey) {
      toast({
        title: "Wallet not connected",
        description: "Please connect your wallet first",
        variant: "destructive",
      });
      return;
    }

    const validationError = validateInputs();
    if (validationError) {
      toast({
        title: "Invalid input",
        description: validationError,
        variant: "destructive",
      });
      return;
    }

    if (!showWarning) {
      setShowWarning(true);
      return;
    }

    setLoading(true);
    try {
      const accountOrMintPubkey = new PublicKey(accountOrMintAddress);
      const newAuthorityPubkey = new PublicKey(newAuthorityAddress);

      console.log("Setting up authority transfer...", {
        accountOrMint: accountOrMintAddress,
        authorityType: selectedAuthority,
        newAuthority: newAuthorityAddress,
        currentAuthority: publicKey.toString(),
      });

      const programId = await getTokenProgramId(
        connection,
        accountOrMintPubkey
      );
      console.log("Using token program ID:", programId.toString());

      const setAuthorityInstruction = createSetAuthorityInstruction(
        accountOrMintPubkey,
        publicKey,
        selectedAuthority,
        newAuthorityPubkey,
        [],
        programId
      );

      console.log("Authority instruction created:", {
        account: accountOrMintPubkey.toString(),
        currentAuthority: publicKey.toString(),
        authorityType: selectedAuthority,
        newAuthority: newAuthorityPubkey.toString(),
        programId: programId.toString(),
      });

      const transaction = new Transaction().add(setAuthorityInstruction);
      transaction.feePayer = publicKey;

      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash("confirmed");
      transaction.recentBlockhash = blockhash;

      console.log("Transaction prepared:", {
        feePayer: transaction.feePayer?.toString(),
        blockhash: transaction.recentBlockhash,
        instructionCount: transaction.instructions.length,
      });

      await processTransaction(transaction, blockhash, lastValidBlockHeight);
    } catch (error) {
      console.error("Error transferring authority:", error);

      let errorMessage = "Failed to transfer authority";

      if (error instanceof Error) {
        if (error.message.includes("User rejected")) {
          errorMessage = "Transaction was rejected by user";
        } else if (
          error.message.includes("insufficient funds") ||
          error.message.includes("InsufficientFunds")
        ) {
          errorMessage = "Insufficient SOL balance for transaction fees";
        } else if (
          error.message.includes("Invalid authority") ||
          error.message.includes("InvalidAuthority")
        ) {
          errorMessage =
            "Current wallet does not have authority to perform this operation";
        } else if (
          error.message.includes("Account not found") ||
          error.message.includes("AccountNotFound")
        ) {
          errorMessage =
            "Token account or mint not found. Please verify the address.";
        } else if (error.message.includes("Invalid token account")) {
          errorMessage = error.message;
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: "Transfer failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeAuthority = async () => {
    if (!publicKey || !selectedAuthority) return;

    setLoading(true);
    try {
      const accountOrMintPubkey = new PublicKey(accountOrMintAddress);

      const programId = await getTokenProgramId(
        connection,
        accountOrMintPubkey
      );

      const revokeAuthorityInstruction = createSetAuthorityInstruction(
        accountOrMintPubkey,
        publicKey,
        selectedAuthority,
        null,
        [],
        programId
      );

      const transaction = new Transaction().add(revokeAuthorityInstruction);
      transaction.feePayer = publicKey;

      const { blockhash } = await connection.getLatestBlockhash("confirmed");
      transaction.recentBlockhash = blockhash;

      const signature = await sendTransaction(transaction, connection, {
        maxRetries: 3,
        preflightCommitment: "confirmed",
      });

      console.log("Authority revocation transaction sent:", signature);

      const confirmation = await connection.confirmTransaction(
        {
          signature,
          blockhash,
          lastValidBlockHeight: (
            await connection.getLatestBlockhash()
          ).lastValidBlockHeight,
        },
        "confirmed"
      );

      if (confirmation.value.err) {
        throw new Error(
          `Transaction failed: ${JSON.stringify(confirmation.value.err)}`
        );
      }

      toast({
        title: "Authority revoked successfully! 🎉",
        description: `${selectedAuthorityType?.label} has been permanently revoked`,
        variant: "default",
      });

      setShowWarning(false);
      onAuthorityChanged?.();
    } catch (error) {
      console.error("Error revoking authority:", error);
      toast({
        title: "Revocation failed",
        description:
          error instanceof Error ? error.message : "Failed to revoke authority",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentPause = async () => {
    if (!publicKey) {
      toast({
        title: "Wallet not connected",
        description: "Please connect your wallet first",
        variant: "destructive",
      });
      return;
    }

    if (!showPermanentPauseWarning) {
      setShowPermanentPauseWarning(true);
      return;
    }

    setLoading(true);
    try {
      const mintPubkey = new PublicKey(defaultMintAddress);

      const programId = await getTokenProgramId(connection, mintPubkey);
      console.log(
        "Using token program ID for permanent pause:",
        programId.toString()
      );

      const revokeMintAuthorityInstruction = createSetAuthorityInstruction(
        mintPubkey,
        publicKey,
        0,
        null,
        [],
        programId
      );

      const transaction = new Transaction().add(revokeMintAuthorityInstruction);
      transaction.feePayer = publicKey;

      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash("confirmed");
      transaction.recentBlockhash = blockhash;

      console.log("Permanent pause transaction prepared:", {
        mint: mintPubkey.toString(),
        authority: publicKey.toString(),
        programId: programId.toString(),
      });

      try {
        console.log("Simulating permanent pause transaction...");
        const simulationResult = await connection.simulateTransaction(
          transaction
        );

        if (simulationResult.value.err) {
          const errorMsg = JSON.stringify(simulationResult.value.err);
          console.error("Permanent pause simulation failed:", errorMsg);
          throw new Error(`Permanent pause simulation failed: ${errorMsg}`);
        }
        console.log("✅ Permanent pause simulation successful");
      } catch (simError) {
        console.error("Permanent pause simulation error:", simError);
        if (
          simError instanceof Error &&
          simError.message.includes("simulation failed")
        ) {
          throw simError;
        }
      }

      console.log("Sending permanent pause transaction to wallet...");

      const signature = await sendTransaction(transaction, connection, {
        maxRetries: 3,
        preflightCommitment: "confirmed",
        skipPreflight: false,
      });

      console.log("Permanent pause transaction sent:", signature);

      console.log("Waiting for permanent pause confirmation...");
      const confirmation = await connection.confirmTransaction(
        {
          signature,
          blockhash,
          lastValidBlockHeight,
        },
        "confirmed"
      );

      if (confirmation.value.err) {
        const errorMsg = JSON.stringify(confirmation.value.err);
        console.error("Permanent pause transaction failed:", errorMsg);
        throw new Error(`Permanent pause failed: ${errorMsg}`);
      }

      console.log(
        "✅ Permanent pause confirmed - Token supply is now permanently frozen"
      );

      toast({
        title: "Token permanently paused! 🔒",
        description:
          "Mint authority has been permanently revoked. No new tokens can ever be minted.",
        variant: "default",
      });

      setShowPermanentPauseWarning(false);
      onAuthorityChanged?.();
    } catch (error) {
      console.error("Error applying permanent pause:", error);

      let errorMessage = "Failed to apply permanent pause";

      if (error instanceof Error) {
        if (error.message.includes("User rejected")) {
          errorMessage = "Transaction was rejected by user";
        } else if (
          error.message.includes("insufficient funds") ||
          error.message.includes("InsufficientFunds")
        ) {
          errorMessage = "Insufficient SOL balance for transaction fees";
        } else if (
          error.message.includes("Invalid authority") ||
          error.message.includes("InvalidAuthority")
        ) {
          errorMessage =
            "Current wallet does not have mint authority to perform this operation";
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: "Permanent pause failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Card className="bg-gradient-card border-border shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Authority Management
          </CardTitle>
          <CardDescription>
            Admin access required to manage token authorities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              Only token administrators can transfer authorities
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="authority-management-step-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="section_03">
          <div className="row">
            <div className="col-md-12">
              <Card className="card_01">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Authority Management
                  </CardTitle>
                  <CardDescription>
                    Transfer or revoke token authorities. These operations are
                    permanent and irreversible.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {envMintAddress ? (
                    <div className="environment-config-wrapper">
                      <div className="d-flex align-items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-success" />
                        <span className="text-sm font-medium text-success">
                          Environment Configured
                        </span>
                      </div>
                      <p className="text-xs text-success/80 mt-1">
                        Mint address loaded
                      </p>
                      <p className="text-xs font-mono text-success/70 mt-1">
                        {envMintAddress.slice(0, 12)}...
                        {envMintAddress.slice(-12)}
                      </p>
                    </div>
                  ) : (
                    <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-warning" />
                        <span className="text-sm font-medium text-warning">
                          Environment Not Configured
                        </span>
                      </div>
                      <p className="text-xs text-warning/80 mt-1">
                        REACT_APP_SPL_TOKEN_MINT_ADDRESS not set in environment
                        variables
                      </p>
                    </div>
                  )}

                  <div className="space-y-2 form-group">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="accountOrMint">{getAddressLabel()}</Label>
                      <div className="flex gap-2">
                        {selectedAuthority !== null && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setUseCustomAddress(!useCustomAddress)
                            }
                            className="text-xs"
                          >
                            {useCustomAddress ? "Use Auto" : "Custom"}
                          </Button>
                        )}
                      </div>
                    </div>
                    <Input
                      id="accountOrMint"
                      placeholder={getAddressPlaceholder()}
                      value={accountOrMintAddress}
                      onChange={(e) => setAccountOrMintAddress(e.target.value)}
                      className="font-mono text-sm"
                      disabled={!isAddressEditable()}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {getAddressHelpText()}
                    </p>
                  </div>

                  <div className="space-y-2 form-group">
                    <Label htmlFor="authorityType">Authority Type</Label>
                    <Select
                      value={selectedAuthority?.toString() || ""}
                      onValueChange={(value) => {
                        const authorityType = parseInt(value);
                        setSelectedAuthority(authorityType);
                        setShowWarning(false);
                      }}
                      className="custom-select"
                    >
                      <SelectTrigger className="custom-select">
                        <SelectValue placeholder="Select authority type to transfer" />
                      </SelectTrigger>
                      <SelectContent
                        className="custom-select-content"
                        style={{
                          border: "1px solid rgba(131, 131, 131, 0.25)",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        }}
                      >
                        {AUTHORITY_TYPES.map((type) => (
                          <SelectItem
                            key={type.value}
                            value={type.value.toString()}
                            className="custom-select-item"
                          >
                            <div className="d-flex align-items-center gap-2">
                              {type.icon}
                              <div>
                                <div className="font-medium">{type.label}</div>
                                <div className="text-xs text-muted-foreground">
                                  {type.description}
                                </div>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 form-group">
                    <Label htmlFor="newAuthority">New Authority Address</Label>
                    <Input
                      id="newAuthority"
                      placeholder="Enter new authority address (leave empty to revoke)"
                      value={newAuthorityAddress}
                      onChange={(e) => setNewAuthorityAddress(e.target.value)}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs mt-2">
                      The wallet address that will receive the authority. Leave
                      empty to permanently revoke the authority.
                    </p>
                  </div>

                  {selectedAuthorityType && showWarning && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-4 bg-warning/10 border border-warning/20 rounded-lg"
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-warning mb-1">
                            ⚠️ Important Warning
                          </h4>
                          <p className="text-sm text-warning/80 mb-3">
                            {selectedAuthorityType.warning}
                          </p>
                          <p className="text-xs mt-2">
                            Make sure you trust the new authority address and
                            have verified it multiple times.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <Separator />

                  <div className="flex gap-3">
                    {newAuthorityAddress.trim() ? (
                      <Button
                        onClick={handleTransferAuthority}
                        disabled={loading || selectedAuthority === null}
                        className="flex-1 btn-style"
                        variant={showWarning ? "destructive" : "default"}
                      >
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                            Transferring...
                          </div>
                        ) : showWarning ? (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            Confirm Transfer
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <UserCheck className="h-4 w-4" />
                            Transfer Authority
                          </div>
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleRevokeAuthority}
                        disabled={loading || selectedAuthority === null}
                        className="flex-1 btn-style"
                      >
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                            Revoking...
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            Revoke Authority
                          </div>
                        )}
                      </Button>
                    )}

                    {showWarning && (
                      <Button
                        onClick={() => setShowWarning(false)}
                        variant="outline"
                        className="flex-1 btn-style"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>

                  {selectedAuthorityType && (
                    <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        {selectedAuthorityType.icon}
                        {selectedAuthorityType.label} Information
                      </h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        {selectedAuthorityType.description}
                      </p>
                      <div className="mt-2 p-2 bg-info/10 border border-info/20 rounded text-xs">
                        <strong>Mint/Freeze Authority:</strong> These
                        authorities operate on the <em>mint address</em> and
                        affect all tokens of this type.
                        {envMintAddress &&
                          " Using the configured mint address from environment variables."}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {envMintAddress && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6"
          >
            <div className="section_03">
              <div className="row">
                <div className="col-md-12">
                  <Card className="card_01">
                    <CardHeader>
                      <CardTitle className="d-flex align-items-center gap-2 text-destructive">
                        <AlertTriangle className="h-5 w-5" />
                        Permanent Token Pause
                      </CardTitle>
                      <CardDescription>
                        Permanently disable token minting by revoking mint
                        authority. This action cannot be undone.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="environment-config-wrapper">
                        <h4 className="font-semibold text-destructive mb-2">
                          ⚠️ IRREVERSIBLE ACTION
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p className="text-destructive/90">
                            <strong>What this does:</strong> Permanently revokes
                            the mint authority, making it impossible to mint new
                            tokens forever.
                          </p>
                          <p className="text-destructive/90">
                            <strong>What this doesn't do:</strong> It does NOT
                            prevent burning. Token holders can still burn their
                            tokens at any time.
                          </p>
                          <p className="text-destructive/90">
                            <strong>Result:</strong> Token supply becomes
                            permanently fixed at current amount.
                          </p>
                        </div>
                      </div>

                      <div className="p-3 bg-muted/50 rounded-lg">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          <h3 className="text-sm font-medium mb-0">
                            Target Mint Address
                          </h3>
                        </div>
                        <p className="font-mono text-xs text-muted-foreground break-all">
                          {envMintAddress}
                        </p>
                      </div>

                      {showPermanentPauseWarning && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="environment-config-wrapper"
                        >
                          <div className="d-flex align-items-start gap-3 ">
                            <AlertTriangle className="h-6 w-6 text-destructive flex-shrink-0 mt-0.5" />
                            <div>
                              <h4 className="font-bold text-destructive mb-2">
                                🚨 FINAL WARNING 🚨
                              </h4>
                              <div className="space-y-2 text-sm">
                                <p className="text-destructive font-medium">
                                  You are about to PERMANENTLY disable token
                                  minting. This action:
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-destructive/90">
                                  <li>Cannot be reversed or undone</li>
                                  <li>Will prevent any future token minting</li>
                                  <li>
                                    Makes the token supply permanently fixed
                                  </li>
                                  <li>
                                    Removes your ability to mint more tokens
                                  </li>
                                </ul>
                                <p className="text-destructive font-medium mt-3">
                                  Are you absolutely sure you want to proceed?
                                </p>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      <div className="d-flex gap-3 pt-2">
                        <Button
                          onClick={handlePermanentPause}
                          disabled={loading}
                          variant="destructive"
                          className="flex-1 btn-style"
                        >
                          {loading ? (
                            <div className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                              Applying Permanent Pause...
                            </div>
                          ) : showPermanentPauseWarning ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4" />
                              YES, PERMANENTLY PAUSE TOKEN
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4" />
                              Apply Permanent Pause
                            </div>
                          )}
                        </Button>

                        {showPermanentPauseWarning && (
                          <Button
                            onClick={() => setShowPermanentPauseWarning(false)}
                            className="border-muted-foreground/20 btn-style transparent"
                          >
                            Cancel
                          </Button>
                        )}
                      </div>

                      <div className="environment-config-wrapper mt-5">
                        <span className="font-medium text-info mb-2">
                          Technical Details
                        </span>
                        <div className="text-xs text-info/80 space-y-1">
                          <p>
                            • This operation revokes the mint authority by
                            setting it to null
                          </p>
                          <p>
                            • Works with both standard SPL Token and Token
                            Extensions programs
                          </p>
                          <p>
                            • The transaction is simulated first to ensure it
                            will succeed
                          </p>
                          <p>
                            • Burning remains possible as it's a token holder
                            operation, not an authority operation
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default AuthorityManagement;
